import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { initVisitorTracker, trackPageview, getLastVisitedPath } from "@/lib/visitorTracker";

const FN_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/track-visitor`;
const ANON = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
const RESUME_KEY = "vss_visitor_resume_shown";

/**
 * Mounted once at app root. Boots the anonymous tracker, logs every
 * route change as a pageview, and shows a one-time "welcome back" toast
 * to returning visitors who landed back on `/`.
 */
export default function VisitorTrackerBoot() {
  const location = useLocation();
  const { toast } = useToast();

  // boot once
  useEffect(() => {
    initVisitorTracker({ endpoint: FN_URL, anonKey: ANON });
  }, []);

  // pageview per route change
  useEffect(() => {
    trackPageview(location.pathname + location.search);
  }, [location.pathname, location.search]);

  // welcome-back toast (only on home, only once per visit)
  useEffect(() => {
    if (location.pathname !== "/") return;
    let shown = false;
    try {
      shown = sessionStorage.getItem(RESUME_KEY) === "1";
    } catch {
      shown = true;
    }
    if (shown) return;
    const last = getLastVisitedPath();
    if (!last || last === "/" || last.startsWith("/auth") || last.startsWith("/dashboard")) return;
    const t = window.setTimeout(() => {
      try {
        sessionStorage.setItem(RESUME_KEY, "1");
      } catch {
        /* ignore */
      }
      toast({
        title: "Welcome back ☀️",
        description: "Pick up where you left off?",
        action: undefined,
      });
      // Provide a manual link via console only — toast action requires a JSX el; keep minimal
      // Users can click through nav as normal.
    }, 1500);
    return () => window.clearTimeout(t);
  }, [location.pathname, toast]);

  return null;
}
